import { policyType, rejectionTypeToPolicyTypeMap } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { searchForNewerStaffEditedOffer } from '../search-store/search.service';
import { GET_MOST_RECENT_STAFF_EDITED_OFFER, GET_POLICY_FROM_OFFER_ID } from './offer-queries';

/**
 * Best-effort lookup of the policy bound from an offer (GitHub issue #31622).
 * Returns `{ id, accountId }` when the offer has already been purchased, or null when it
 * hasn't — and also null on any lookup failure, so the warning banner never blocks or
 * breaks the offer page.
 */
export const fetchBoundPolicy = async (client, offerId) => {
  try {
    const { data, error } = await client.query({
      query: GET_POLICY_FROM_OFFER_ID,
      variables: { offerId },
      fetchPolicy: 'no-cache'
    });
    if (error || !data?.getPolicyFromOfferId) {
      return null;
    }
    const { id, accountId } = data.getPolicyFromOfferId;
    return { id, accountId };
  } catch {
    return null;
  }
};

/**
 * Guard for late-arriving bound-policy lookups (#31622): a result may only be applied if
 * it belongs to the most recently REQUESTED offer. Comparing against the loaded offer's id
 * instead would discard the legitimate result whenever the (fast, GSI-backed) lookup wins
 * the race against the heavy GET_OFFER query — i.e. nearly always, and always on first load.
 */
export const shouldApplyBoundPolicyResult = (forOfferId, latestRequestedOfferId) =>
  forOfferId === undefined || forOfferId === latestRequestedOfferId;

// HB/AB are bundle-discounted monoline options for offers linked to an existing customer:
// the shared link carries policyType=HA, but quote-fe only binds the H/A half (it honors
// noBindAuto/noBindHome for existing customers), so rejections gate on that half alone.
const optionToBoundPolicyType = {
  [policyType.HomeBundle]: policyType.Home,
  [policyType.AutoBundle]: policyType.Auto
};

/**
 * Whether the given option is affected by a soft rejection, scoped via
 * rejectionTypeToPolicyTypeMap so a rejection on the opposite policy type doesn't block a
 * clean monoline option (GitHub issue #31905). Rejection types without a map entry don't
 * block, matching the checkout rejectionsSchema and the CannotPurchase notification.
 */
export const hasSoftRejectionForOption = (rejections, selectedOption) => {
  const boundPolicyType = optionToBoundPolicyType[selectedOption] ?? selectedOption;
  return Boolean(
    rejections?.some(
      (rejection) =>
        rejection.level === 'soft' && rejectionTypeToPolicyTypeMap[rejection.type]?.includes(boundPolicyType)
    )
  );
};

/**
 * Whether a rejection HARD-blocks the given option from moving forward through the offer's
 * checkout / pre-bind validation (the `rejectionsSchema` gate).
 *
 * A rejection blocks when its type maps to the selected option — EXCEPT a soft rejection on a
 * farm-based option (F/Z/Y). Farm offers resolve soft rejections through the UW-review flow and gate
 * checkout separately on UW approval (the "Continue to checkout" button is disabled until approved),
 * so a soft farm rejection must NOT block here — otherwise the agent is trapped before they can even
 * reach the "Continue to Underwriting Review" form (GitHub issue #33355, e.g. the EQ Zone-2 approval
 * rejection 50133). Home/auto keep blocking on soft rejections: for them this predicate IS the
 * checkout gate, and UW approval clears the rejection rather than bypassing it.
 */
export const rejectionBlocksOptionCheckout = (rejection, selectedOption) => {
  if (!rejectionTypeToPolicyTypeMap[rejection.type]?.some((type) => type === selectedOption)) {
    return false;
  }
  if (isFarmBasedPolicyType(selectedOption) && rejection.level === 'soft') {
    return false;
  }
  return true;
};

// Newer-offer lookup for the auto-redirect: FE Meilisearch first, then the
// staff-appsync-be query for browsers that can't reach Meilisearch (partner firewalls on
// comparative-rater bridges). Never throws — returns null so the lookup can't block getOffer.
export const findNewerStaffEditedOffer = async (client, currentOffer, userPreferences) => {
  try {
    return await searchForNewerStaffEditedOffer(currentOffer, userPreferences);
  } catch (error) {
    console.warn('FE search for newer offer failed, falling back to backend query:', error);
  }
  try {
    const response = await client.query({
      query: GET_MOST_RECENT_STAFF_EDITED_OFFER,
      variables: { webUserId: currentOffer.webUserId },
      fetchPolicy: 'no-cache'
    });
    const mostRecent = response.data?.getMostRecentStaffEditedOffer;
    // The backend doesn't know which offer is being viewed — apply the strictly-newer gate here
    const isStrictlyNewer =
      mostRecent?.updatedDateTime &&
      (!currentOffer.updatedDateTime ||
        new Date(mostRecent.updatedDateTime).getTime() > new Date(currentOffer.updatedDateTime).getTime());
    if (mostRecent?.offerId && mostRecent.offerId !== currentOffer.id && isStrictlyNewer) {
      return {
        id: mostRecent.offerId,
        webUserId: currentOffer.webUserId,
        updatedDateTime: mostRecent.updatedDateTime
      };
    }
    return null;
  } catch (fallbackError) {
    console.warn('Backend fallback for newer offer failed:', fallbackError);
    return null;
  }
};

export const pollPolicyTable = async (client, offerId, { maxRetries }) => {
  let currentAttempt = 0;
  while (currentAttempt < maxRetries) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      currentAttempt += 1;
      const { data, error } = await client.query({
        query: GET_POLICY_FROM_OFFER_ID,
        variables: { offerId }
      });
      if (error) {
        throw error;
      }
      const { accountId } = data.getPolicyFromOfferId;
      return { data: { accountId } };
    } catch (error) {
      if (currentAttempt >= maxRetries) {
        throw error;
      }
    }
  }
};
